
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { addToMarketingList } from "@/services/brevoService";
import { useAnalytics } from "@/hooks/useAnalytics";
import { supabase } from "@/integrations/supabase/client";

interface NewsletterSubscriptionProps {
  language?: 'fi' | 'en' | 'sv' | 'ar' | 'he';
}

interface LanguageTexts {
  title: string;
  emailLabel: string;
  nameLabel: string;
  buttonText: string;
  loadingText: string;
  consentText: string;
  successMessage: string;
  errorMessage: string;
}

const languageTexts: Record<string, LanguageTexts> = {
  fi: {
    title: "Liity postituslistalle",
    emailLabel: "Sähköposti",
    nameLabel: "Nimi",
    buttonText: "Tilaa",
    loadingText: "Tilataan...",
    consentText: "Haluan vastaanottaa uutiskirjeitä ja tietoja lainapalveluista",
    successMessage: "Kiitos tilauksestasi! Vahvistusviesti on lähetetty sähköpostiisi.",
    errorMessage: "Tilaus epäonnistui. Yritä uudelleen."
  },
  en: {
    title: "Subscribe to our newsletter",
    emailLabel: "Email",
    nameLabel: "Name",
    buttonText: "Subscribe",
    loadingText: "Subscribing...",
    consentText: "I want to receive newsletters and loan service information",
    successMessage: "Thank you for subscribing! A confirmation has been sent to your email.",
    errorMessage: "Subscription failed. Please try again."
  },
  sv: {
    title: "Prenumerera på vårt nyhetsbrev",
    emailLabel: "E-post",
    nameLabel: "Namn",
    buttonText: "Prenumerera",
    loadingText: "Prenumererar...",
    consentText: "Jag vill ta emot nyhetsbrev och information om lånetjänster",
    successMessage: "Tack för din prenumeration! En bekräftelse har skickats till din e-post.",
    errorMessage: "Prenumerationen misslyckades. Försök igen."
  },
  ar: {
    title: "اشترك في نشرتنا الإخبارية",
    emailLabel: "البريد الإلكتروني",
    nameLabel: "الاسم",
    buttonText: "اشترك",
    loadingText: "جاري الاشتراك...",
    consentText: "أرغب في تلقي النشرات الإخبارية ومعلومات خدمة القروض",
    successMessage: "شكرا لاشتراكك! تم إرسال تأكيد إلى بريدك الإلكتروني.",
    errorMessage: "فشل الاشتراك. يرجى المحاولة مرة أخرى."
  },
  he: {
    title: "הירשם לnewsletter שלנו",
    emailLabel: "דוא״ל",
    nameLabel: "שם",
    buttonText: "הירשם",
    loadingText: "נרשם...",
    consentText: "אני רוצה לקבל עדכונים ומידע על שירותי הלוואות",
    successMessage: "תודה שנרשמת! אישור נשלח לדוא״ל שלך.",
    errorMessage: "ההרשמה נכשלה. נסה שוב."
  }
};

const InternationalNewsletterSubscription: React.FC<NewsletterSubscriptionProps> = ({ 
  language = 'fi' 
}) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const { trackEvent } = useAnalytics();
  
  const texts = languageTexts[language] || languageTexts.en;
  const isRtl = language === 'ar' || language === 'he';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Email is required");
      return;
    }
    
    setIsLoading(true);
    
    try {
      trackEvent('newsletter_subscribe_attempt', { 
        source: 'newsletter_form',
        language 
      });
      
      // First, save to our marketing_contacts table in Supabase
      try {
        await supabase.rpc('insert_marketing_contact', {
          p_email: email,
          p_name: name,
          p_marketing_consent: marketingConsent,
          p_source: `newsletter_${language}`
        });
        console.log("Contact saved to database successfully");
      } catch (error) {
        console.error("Error saving to Supabase:", error);
      }
      
      // Then, sync with Brevo
      await addToMarketingList(
        email,
        name,
        `newsletter_${language}`,
        marketingConsent
      );
      
      // Send welcome email via the edge function
      try {
        console.log('Sending newsletter welcome email');
        const { data: emailData, error: emailError } = await supabase.functions.invoke('send-contact-email', {
          body: {
            name: name || email.split('@')[0],
            email: email,
            isNewsletter: true
          }
        });
        
        if (emailError) {
          console.error('Error sending newsletter welcome email:', emailError);
        } else {
          console.log('Newsletter welcome email sent:', emailData);
        }
      } catch (emailErr) {
        console.error('Failed to send newsletter welcome email:', emailErr);
      }
      
      trackEvent('newsletter_subscribe_success', { language });
      toast.success(texts.successMessage);
      
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setEmail("");
        setName("");
      }, 3000);
      
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      trackEvent('newsletter_subscribe_error', { 
        error: error instanceof Error ? error.message : String(error),
        language
      });
      toast.error(texts.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      dir={isRtl ? "rtl" : "ltr"}
    >
      {isSuccess ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-50 border border-green-200 rounded-lg p-4 text-center"
        >
          <div className="text-green-600 font-medium mb-2">{texts.successMessage}</div>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-700">
              {texts.nameLabel}
            </Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={texts.nameLabel}
              className="border-gray-300 focus:border-blue-400"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">
              {texts.emailLabel}
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={texts.emailLabel}
              className="border-gray-300 focus:border-blue-400"
              required
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="marketingConsent" 
              checked={marketingConsent}
              onCheckedChange={(checked) => setMarketingConsent(checked as boolean)}
              className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
            />
            <label
              htmlFor="marketingConsent"
              className="text-sm text-gray-700 font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {texts.consentText}
            </label>
          </div>
          
          <Button 
            type="submit" 
            disabled={isLoading} 
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? texts.loadingText : texts.buttonText}
          </Button>
        </form>
      )}
    </motion.div>
  );
};

export default InternationalNewsletterSubscription;
